import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
const pages = [{
  id: 'cover',
  image: 'https://cdn.magicpatterns.com/uploads/cgH5PTGXcb6WQ2GR3YXGmG/mockup_thenap.png',
  caption: 'The Nap-Trapped Reset — your 7-day tactical guide.'
}, {
  id: 'gentle-practice',
  image: 'https://cdn.magicpatterns.com/uploads/eWbsk6GuqjrGaJGFnidQQz/contenido1.png',
  caption: 'Gentle put-down practice — step by step, no pressure.'
}, {
  id: 'reset-plan',
  image: 'https://cdn.magicpatterns.com/uploads/5SPwzNwf3f2JZj4MJGc34h/contenido2.png',
  caption: 'Your 7-day reset plan for sustainable self-care.'
}, {
  id: 'day-map',
  image: 'https://cdn.magicpatterns.com/uploads/gLBBLsnREFxyVtK8QjaESK/contenido3.png',
  caption: 'The 3-Block Day Map — your concrete daily structure.'
}, {
  id: 'system',
  image: 'https://cdn.magicpatterns.com/uploads/w11FqVa8MKBKh2BznHz4r4/contenido4.png',
  caption: 'How this system works differently — parent first.'
}];
export function PdfPreview() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  // Reset to slide 0 each time section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasBeenVisible) {
        setActiveIndex(0);
        setHasBeenVisible(true);
      }
      if (!entry.isIntersecting) {
        setHasBeenVisible(false);
      }
    }, {
      threshold: 0.2
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasBeenVisible]);
  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(() => {
      setActiveIndex(current => (current + 1) % pages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isHovered]);
  const handleDragEnd = (e: any, {
    offset,
    velocity
  }: PanInfo) => {
    const swipe = offset.x;
    const swipeThreshold = 50;
    if (swipe < -swipeThreshold) {
      setActiveIndex(current => (current + 1) % pages.length);
    } else if (swipe > swipeThreshold) {
      setActiveIndex(current => (current - 1 + pages.length) % pages.length);
    }
  };
  const nextSlide = () => setActiveIndex(current => (current + 1) % pages.length);
  const prevSlide = () => setActiveIndex(current => (current - 1 + pages.length) % pages.length);
  return <section ref={sectionRef} className="relative py-16 md:py-24 px-6 md:px-8 bg-cream-dark overflow-hidden" data-id="element-218">
      {/* Subtle ambient glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-terracotta/4 rounded-full blur-[100px] pointer-events-none" data-id="element-219" />

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1]
    }} className="max-w-3xl mx-auto relative z-10" data-id="element-220">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10" data-id="element-221">
          <div className="w-12 h-px bg-terracotta/30 mx-auto mb-6" data-id="element-222" />
          <div className="inline-flex items-center gap-2 mb-4" data-id="element-223">
            <BookOpen className="w-4 h-4 text-terracotta" data-id="element-224" />
            <p className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold" data-id="element-225">
              Inside The Guide
            </p>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-brown mb-4 leading-tight" data-id="element-226">
            Take a Peek Inside
          </h2>
          <p className="text-brown-light/80 text-base md:text-lg max-w-md mx-auto" data-id="element-227">
            15 pages. Scannable. Built for 3am.
          </p>
        </div>

        <div className="relative group" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-228">
          {/* Image Container */}
          <div className="relative rounded-2xl md:rounded-3xl overflow-hidden bg-white aspect-[4/3] md:aspect-[16/10] border border-brown/5" style={{
          boxShadow: '0 12px 48px rgba(61,50,41,0.08), 0 4px 16px rgba(61,50,41,0.04)'
        }} data-id="element-229">
            <AnimatePresence initial={false} data-id="element-230">
              <motion.img key={activeIndex} src={pages[activeIndex].image} alt={pages[activeIndex].caption} initial={{
              opacity: 0,
              scale: 1.01
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.99
            }} transition={{
              opacity: {
                duration: 1.2,
                ease: [0.4, 0, 0.2, 1]
              },
              scale: {
                duration: 1.4,
                ease: [0.4, 0, 0.2, 1]
              }
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.2} onDragEnd={handleDragEnd} className="absolute inset-0 w-full h-full object-cover cursor-grab active:cursor-grabbing" data-id="element-231" />
            </AnimatePresence>

            {/* Slide counter badge */}
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md border border-brown/10 text-brown text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full pointer-events-none shadow-sm" data-id="element-232">
              {activeIndex + 1} / {pages.length}
            </div>

            {/* Navigation Arrows */}
            <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white backdrop-blur-md border border-brown/10 rounded-full flex items-center justify-center text-brown/60 hover:text-brown shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0" aria-label="Previous slide" data-id="element-233">
              <ChevronLeft className="w-5 h-5" data-id="element-234" />
            </button>
            <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white backdrop-blur-md border border-brown/10 rounded-full flex items-center justify-center text-brown/60 hover:text-brown shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0" aria-label="Next slide" data-id="element-235">
              <ChevronRight className="w-5 h-5" data-id="element-236" />
            </button>

            {/* Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-brown/5" data-id="element-237">
              <motion.div key={activeIndex} initial={{
              width: '0%'
            }} animate={{
              width: isHovered ? '0%' : '100%'
            }} transition={{
              duration: 4,
              ease: 'linear'
            }} className="h-full bg-terracotta/40" data-id="element-238" />
            </div>
          </div>

          {/* Ground shadow */}
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-3/4 h-6 bg-brown/8 blur-xl rounded-full pointer-events-none" data-id="element-239" />

          {/* Caption */}
          <div className="h-14 mt-6 flex items-center justify-center" data-id="element-240">
            <AnimatePresence mode="wait" data-id="element-241">
              <motion.p key={activeIndex} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.6,
              ease: [0.25, 0.1, 0.25, 1]
            }} className="font-serif text-base md:text-lg text-brown/70 italic text-center px-4" data-id="element-242">
                {pages[activeIndex].caption}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-3 mt-1" data-id="element-243">
            {pages.map((_, idx) => <button key={idx} onClick={() => setActiveIndex(idx)} aria-label={`Go to slide ${idx + 1}`} className="p-2 -m-2 transition-all duration-300" data-id="element-244">
                <div className={`rounded-full transition-all duration-500 ease-out ${idx === activeIndex ? 'w-7 h-2 bg-terracotta rounded-full' : 'w-2 h-2 bg-brown/20 hover:bg-brown/40'}`} data-id="element-245" />
              </button>)}
          </div>
        </div>
      </motion.div>
    </section>;
}